import {
  FetchMiddleware,
  FetchParams,
  FuncWithContext,
  RequestContext,
  RequestContextResult,
  ResponseErrorHandler,
  ResponseHandler,
} from './types';
import { HttpClient, withHttpMethods } from './http';

type FullContext<C, RC extends RequestContext<C>[]> = Readonly<
  C & RequestContextResult<RC>
>;
type ChirpFetchProps<C, RC extends RequestContext<C>[]> = {
  context?: C;
  requestContext?: RC;
  middleware?: FetchMiddleware<FullContext<C, RC>>[];
  onResponse?: ResponseHandler<FullContext<C, RC>>[];
  onResponseError?: ResponseErrorHandler<FullContext<C, RC>>[];
};

export function composeFunctionsWithContext<T, C, R extends T = T>(
  functions?: FuncWithContext<T, C, R>[]
): FuncWithContext<T, C, R> {
  if (!functions || functions.length === 0) {
    return ((arg: T, __: C) => arg) as FuncWithContext<T, C, R>;
  }

  return functions.reduce(
    (prevFn, nextFn) => (arg: T, context: C) =>
      nextFn(prevFn(arg, context), context),
    (arg: T, __: C) => arg
  ) as FuncWithContext<T, C, R>;
}

function createRequestContext<C, Fns extends RequestContext<C>[]>(
  functions: Fns,
  params: FetchParams,
  context: C
): RequestContextResult<Fns> {
  if (!functions || functions.length === 0) {
    return {} as RequestContextResult<Fns>;
  }
  const result = {} as RequestContextResult<Fns>;

  for (const fn of functions) {
    Object.assign(result, fn(params, context));
  }

  return result;
}

/**
 * Creates a wrapped fetch with context available in each part of the process.
 *
 * - Context: Defined on fetch init. Passed down into each function.
 * - Request Context: Functions that run each request to produce additional context.
 *                    Base context and fetch input are passed to each function.
 * - Middleware: Runs before fetch on the parameters to the fetch.
 * - Response Handler: Runs in the `.then` of the fetch result.
 * - Response Error Handler: Runs in the `.catch` of the fetch result.
 *                           Note that API errors don't get caught by default,
 *                           use the `throwOnFailed` handler to make such errors throw.
 *
 * Each of the middleware/response handlers is expected to return
 * the same type that it takes in the first argument. They are not expected to
 * be pure functions as they may have side-effects. They are reverse composed
 * (`[a, b, c]` --> `c.b.a`) before run on the input/response.
 *
 * Each function can define a slice of "context" that it will use. Context can
 * exist either at the fetcher level (created on fetcher init) or at the
 * request level (created when a request is made). The "context" parameter
 * defines fetcher context and is expected to be a record. The "requestContext"
 * parameter is expected to be an array of record-returning functions that are
 * run each time a request is made to build a requestContext object. Each
 * function is then passed the union of these two objects.
 *
 * The fetcher is type-safe with respect to the context and will produce an
 * error if any of the middleware/response handlers use context that does not exist
 * in the full context object.
 */
export function createWrappedFetch<
  C extends Record<string, any>,
  Fns extends RequestContext<C>[],
>({
  context = {} as C,
  requestContext = [] as unknown as Fns,
  middleware,
  onResponse,
  onResponseError,
}: ChirpFetchProps<C, Fns>): HttpClient {
  const composedMiddleware = composeFunctionsWithContext(middleware);
  const composedRespHandlers = composeFunctionsWithContext(onResponse);
  const composedRespErrorHandlers =
    composeFunctionsWithContext(onResponseError);

  const fetchFn = (
    input: RequestInfo | URL,
    init?: RequestInit | undefined
  ) => {
    const reqContext = createRequestContext(
      requestContext,
      { input, init },
      context
    );

    // TODO: Do we need to freeze this at all? Or should this be a deep freeze?
    const fullContext = Object.freeze({ ...context, ...reqContext });
    const processedInput = composedMiddleware({ input, init }, fullContext);

    const handleResponse = (responsePromise: Promise<Response>) =>
      responsePromise
        .then((response) => composedRespHandlers(response, fullContext))
        .catch((reason: any) => {
          composedRespErrorHandlers(reason, fullContext);
          throw reason;
        });

    return handleResponse(fetch(processedInput.input, processedInput.init));
  };

  return withHttpMethods(fetchFn);
}
